import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { injectIntl } from 'react-intl';

import { fetchUserProfile } from '../../actions/Auth';

import { APIupdateSettings } from '../../api/Settings';

import AdminFiltersHeader from '../../components/Admin/Filters/Header';
import AdminFiltersList from '../../components/Admin/Filters/List';
import AdminEditButtons from '../../components/Admin/EditButtons';

import { addFlashMessage } from '../../helpers/FlashMessages';
import { getSetting } from '../../helpers/Settings';

import Loading from '../../components/Loading';
import ErrorBoundary from '../../components/ErrorBoundary';
import HelpIcon from '../../components/HelpIcon';

const arrayMoveNested = (list, fromKey, toKey) => {
  const cloned = JSON.parse(JSON.stringify(list));

  const findItem = (arr, key, parent = null) => {
    for (let i = 0; i < arr.length; i += 1) {
      const item = arr[i];
      if (item.key === key) {
        return {
          item, index: i, parent, arr,
        };
      }
      if (item.children) {
        const result = findItem(item.children, key, item);
        if (result) return result;
      }
    }
    return null;
  };

  const from = findItem(cloned, fromKey);
  const to = findItem(cloned, toKey);
  if (!from || !to) return list;

  const fromIsChild = /^tax_group_\d+_tax_\d+$/.test(from.item.key);

  if (fromIsChild && (from.parent?.key !== to.parent?.key)) {
    return list;
  } if (from.parent !== null || to.parent !== null) {
    return list;
  }

  const [movedItem] = from.arr.splice(from.index, 1);
  to.arr.splice(to.index, 0, movedItem);

  return cloned;
};

const isTaxKey = key => /^tax_\d+$/.test(key);
const isGroupKey = key => /^tax_group_\d+$/.test(key);
const isOpenByDefault = value => ![false, 0, '0', 'false'].includes(value);

class AdminFilters extends Component {
  constructor(props) {
    super(props);

    this.state = {
      fileFilters: [],
      saving: false,
      unsaved: false,
    };

    this.moveFileFilter = this.moveFileFilter.bind(this);
    this.handleVisibilitySwitch = this.handleVisibilitySwitch.bind(this);
    this.handleAdminsOnlySwitch = this.handleAdminsOnlySwitch.bind(this);
    this.handleOpenByDefaultSwitch = this.handleOpenByDefaultSwitch.bind(this);
    this.handleSave = this.handleSave.bind(this);
  }

  componentDidMount() {
    this.loadFileFilters();
  }

  componentDidUpdate() {
    this.loadFileFilters();
  }

  getTaxonomyName(taxonomyId) {
    const { taxonomies } = this.props;
    const taxonomy = taxonomies.find(t => (t.id === taxonomyId));
    return taxonomy ? taxonomy.plural : null;
  }

  getMetadataGroupName(metadataGroupId) {
    const { metadataGroups } = this.props;
    const metadataGroup = metadataGroups.find(t => (t.id === metadataGroupId));
    return metadataGroup.name;
  }

  loadFileFilters() {
    const {
      intl,
      taxonomies,
      metadataGroups,
      taxonomiesLoading,
      metadataGroupsLoading,
    } = this.props;
    const { fileFilters: fileFiltersState } = this.state;

    if (taxonomiesLoading || metadataGroupsLoading || fileFiltersState.length > 0) {
      return;
    }

    let fileFilters = getSetting('filters_files');

    const taxonomyFilters = [];
    const metadataGroupsFilters = [];
    fileFilters = fileFilters
      .filter(f => {
        if (isTaxKey(f.key)) {
          return taxonomies.some(t => t.id ===
            Number(f.key.replace('tax_', '')));
        }
        if (isGroupKey(f.key)) {
          return metadataGroups.some(g => g.id ===
            Number(f.key.replace('tax_group_', '')));
        }
        return true;
      })
      .map((f) => {
        const newF = { ...f };
        if (isTaxKey(f.key)) {
          const taxonomyId = parseInt(f.key.replace('tax_', ''));
          const taxonomyName = this.getTaxonomyName(taxonomyId);
          if (taxonomyName) {
            taxonomyFilters.push(taxonomyId);
            if (!f.name) {
              newF.name = `${taxonomyName} (${intl.formatMessage({ defaultMessage: 'Tag Category' })})`;
            }
          } else {
            newF.name = null;
          }
        }
        if (isGroupKey(f.key)) {
          const metadataGroupId = parseInt(f.key.replace('tax_group_', ''));
          const metadataGroupName = this.getMetadataGroupName(metadataGroupId);
          newF.open_by_default = isOpenByDefault(f.open_by_default);
          if (metadataGroupName) {
            metadataGroupsFilters.push(metadataGroupId);
            if (!f.name) {
              newF.name = `${metadataGroupName} (${intl.formatMessage({ defaultMessage: 'Metadata Group' })})`;
            }
          } else {
            newF.name = null;
          }
        }
        return newF;
      }).filter(f => (f.name));

    taxonomies.forEach((tax) => {
      if (!taxonomyFilters.includes(tax.id)) {
        fileFilters.push({
          key: `tax_${tax.id}`,
          name: `${this.getTaxonomyName(tax.id)} (${intl.formatMessage({ defaultMessage: 'Tag Category' })})`,
          visible: true,
        });
      }
    });

    metadataGroups.forEach((taxGroup) => {
      if (!metadataGroupsFilters.includes(taxGroup.id)) {
        fileFilters.push({
          key: `tax_group_${taxGroup.id}`,
          name: `${this.getMetadataGroupName(taxGroup.id)}`,
          visible: true,
          open_by_default: true,
          children: taxGroup.taxonomies
            .map(tid => {
              const taxonomy = taxonomies.find(t => t.id === tid);
              if (!taxonomy) return null;
              return {
                key: `tax_group_${taxGroup.id}_tax_${taxonomy.id}`,
                name: `${this.getTaxonomyName(taxonomy.id)} (Tag Category)`,
                visible: true,
              };
            })
            .filter(Boolean),
        });
      }
    });

    this.setState({ fileFilters });
  }

  moveFileFilter(dragKey, hoverKey) {
    this.setState(state => ({
      fileFilters: arrayMoveNested(state.fileFilters, dragKey, hoverKey),
      unsaved: true,
    }));
  }

  handleVisibilitySwitch(key) {
    const toggleVisibility = filters => filters.map((f) => {
      const isGroup = isGroupKey(f.key) && Array.isArray(f.children) && f.children.length > 0;

      if (f.key === key) {
        const visible = !f.visible;
        return {
          ...f,
          visible,
          ...(isGroup && { children: f.children.map(c => ({ ...c, visible })) }),
        };
      }

      if (isGroup) {
        const child = f.children.find(c => c.key === key);
        if (!child) return f;
        const children = f.children.map(c => (
          c.key === key ? { ...c, visible: !child.visible } : c
        ));
        return {
          ...f,
          // A group remains visible while any child is visible.
          visible: children.some(c => c.visible),
          children,
        };
      }

      return f;
    });

    this.setState(prevState => ({
      fileFilters: toggleVisibility(prevState.fileFilters),
      unsaved: true,
    }));
  }

  handleAdminsOnlySwitch(key) {
    const toggleAdminsOnly = filters => filters.map((f) => {
      const isGroup = isGroupKey(f.key) && Array.isArray(f.children) && f.children.length > 0;

      if (f.key === key) {
        const adminsOnly = !f.admins_only;
        return {
          ...f,
          admins_only: adminsOnly,
          ...(isGroup && { children: f.children.map(c => ({ ...c, admins_only: adminsOnly })) }),
        };
      }

      if (isGroup) {
        const child = f.children.find(c => c.key === key);
        if (!child) return f;
        const adminsOnly = !child.admins_only;
        const children = f.children.map(c => (
          c.key === key ? { ...c, admins_only: adminsOnly } : c
        ));
        return {
          ...f,
          // A group is admins-only only when every child is.
          admins_only: children.every(c => c.admins_only),
          children,
        };
      }

      return f;
    });

    this.setState(prevState => ({
      fileFilters: toggleAdminsOnly(prevState.fileFilters),
      unsaved: true,
    }));
  }

  handleOpenByDefaultSwitch(key) {
    if (!isGroupKey(key)) return;

    this.setState(prevState => ({
      fileFilters: prevState.fileFilters.map(f => (
        f.key === key ? { ...f, open_by_default: !isOpenByDefault(f.open_by_default) } : f
      )),
      unsaved: true,
    }));
  }

  handleSave() {
    const {
      intl,
      dispatch,
    } = this.props;
    const { fileFilters } = this.state;

    this.setState({ saving: true });
    APIupdateSettings({
      filters_files: fileFilters.map((f) => {
        const newF = { ...f };
        delete newF.name;
        if (isGroupKey(newF.key)) {
          newF.open_by_default = isOpenByDefault(newF.open_by_default);
        }
        return newF;
      }),
    }).then(() => {
      dispatch(fetchUserProfile()).then(() => {
        addFlashMessage({
          type: 'success',
          text: intl.formatMessage({ defaultMessage: 'Changes have been saved' }),
        });
        this.setState({ saving: false, unsaved: false });
      });
    });
  }

  render() {
    const { intl } = this.props;
    const {
      fileFilters,
      saving,
      unsaved,
    } = this.state;

    return (
      <div className="main-content filters-content settings-inner-content">
        <AdminFiltersHeader />
        <p>
          {intl.formatMessage({ defaultMessage: 'Manage and organize your list of visible file filters.' })}
          <HelpIcon
            id="help-filters"
            url="https://help.stockpress.co/en/articles/5937890-managing-file-filters"
          />
        </p>
        <div className="tab-content settings-tab-content settings-container">
          {!fileFilters ? (
            <Loading />
          ) : (
            <ErrorBoundary>
              <AdminFiltersList
                filters={fileFilters}
                saving={saving}
                moveFilter={this.moveFileFilter}
                handleVisibilitySwitch={this.handleVisibilitySwitch}
                handleAdminsOnlySwitch={this.handleAdminsOnlySwitch}
                handleOpenByDefaultSwitch={this.handleOpenByDefaultSwitch}
                handleSave={this.handleSave}
              />
              <AdminEditButtons
                hideDelete={true}
                handleSave={this.handleSave}
                saving={saving}
                unsaved={unsaved}
              />
            </ErrorBoundary>
          )}
        </div>
      </div>
    );
  }
}

const mapStateToProps = state => ({
  taxonomies: state.Metadata.taxonomies,
  metadataGroups: state.Metadata.metadataGroups,
  taxonomiesLoading: state.Metadata.loadingTaxonomies,
  metadataGroupsLoading: state.Metadata.loadingMetadataGroups,
});

AdminFilters.propTypes = {
  intl: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  taxonomies: PropTypes.array.isRequired,
  metadataGroups: PropTypes.array.isRequired,
  taxonomiesLoading: PropTypes.bool.isRequired,
  metadataGroupsLoading: PropTypes.bool.isRequired,
};

export default connect(mapStateToProps)(injectIntl(AdminFilters));
